/**
 * CollabV3ThreadLinkRow 컴포넌트
 * @description 글의 링크 첨부 줄(앱 3.0 링크 줄) — 그림 · 제목 · 종류. 바깥 주소(URL)는 새 탭으로 열고
 *   제목이 없으면 도메인을 쓴다. 앱 안의 대상(콜랩 · 글 · 스레드 · 작품)은 이름만 보여준다. 볼 수 없는
 *   대상이면 `This link isn't available`.
 * @example <CollabV3ThreadLinkRow link={item.link} />
 */
import { memo } from "react";

import { collabV3ThreadStyles as styles } from "./CollabV3Thread.styles";
import type { CollabV3ThreadLinkRowProps } from "./CollabV3Thread.types";

import { COLLAB_V3_COPY } from "../../constants/collabV3Copy";
import type { CollabV3ThreadLink } from "../../entities";
import { AppImage } from "@/shared/ui";

const copy = COLLAB_V3_COPY.thread.link;
const THUMB_WIDTH = 48;

const hostOf = (href: string): string => new URL(href).hostname;

function labelsOf(link: CollabV3ThreadLink): { title: string; meta: string } {
  if (link.type === "URL") {
    const host = link.href ? hostOf(link.href) : "";
    return { title: link.title ?? host, meta: host };
  }
  const type = copy.type[link.type];
  return { title: link.title ?? type, meta: type };
}

const CollabV3ThreadLinkRow = memo<CollabV3ThreadLinkRowProps>(({ link }) => {
  if (!link.isAvailable) {
    return <p className={`${styles.link} ${styles.linkMeta}`}>{copy.unavailable}</p>;
  }
  const { title, meta } = labelsOf(link);
  const body = (
    <>
      {link.imageUrl ? (
        <span className={styles.linkThumb}>
          <AppImage uri={link.imageUrl} targetWidth={THUMB_WIDTH} />
        </span>
      ) : null}
      <span className={styles.linkTexts}>
        <span className={styles.linkTitle}>{title}</span>
        {meta !== title ? <span className={styles.linkMeta}>{meta}</span> : null}
      </span>
    </>
  );
  if (!link.href) return <div className={styles.link}>{body}</div>;
  return (
    <a
      href={link.href}
      target="_blank"
      rel="noopener noreferrer nofollow ugc"
      className={`${styles.link} ${styles.linkHover}`}
    >
      {body}
    </a>
  );
});

CollabV3ThreadLinkRow.displayName = "CollabV3ThreadLinkRow";

export default CollabV3ThreadLinkRow;
